'use client'

import { useEffect } from 'react'
import { useUser } from '@auth0/nextjs-auth0'
import { useSearchParams } from 'next/navigation'

import { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import { useAnalytics } from '@/app/lib/analytics'

type Props = {
  vessel: TVesselJourneyResponse
}

/**
 * Client component to track vessel page views with analytics
 * This component handles the client-side tracking since we need access to browser APIs
 */
export default function VesselPageAnalytics({ vessel }: Props) {
  const { user } = useUser()
  const searchParams = useSearchParams()
  const { trackVesselViewed } = useAnalytics()

  useEffect(() => {
    // Determine the source of the visit
    let source: 'homepage' | 'bookmark' | 'share_link' | 'direct' = 'direct'

    // Check if coming from share link
    if (window.location.pathname.includes('/share/')) {
      source = 'share_link'
    }
    // Check if there's a referrer that indicates source
    else if (document.referrer) {
      const referrer = new URL(document.referrer)
      if (
        referrer.pathname === '/' ||
        referrer.pathname.includes('/en') ||
        referrer.pathname.includes('/pt')
      ) {
        source = 'homepage'
      }
    }

    // Track vessel viewed
    trackVesselViewed({
      imo: vessel.ship.imo,
      vesselName: vessel.ship?.name || 'Unknown',
      source,
      userType: user ? 'authenticated' : 'anonymous',
      portName: vessel.arrival?.port?.name || 'Unknown Port'
    })
  }, [vessel, user, searchParams, trackVesselViewed])

  // This component doesn't render anything
  return null
}
